import { animated, useSpring, useSprings } from '@react-spring/web';
import cx from 'classnames';
import {
  Children,
  CSSProperties,
  FC,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icon, Typography } from 'src/components-v2/DataDisplay';
import { Link } from 'src/components/Inputs';
import { useOutsideClick } from 'src/hooks';
import { easeInOut } from 'src/lib/config/ease.config';

import { Columns } from '../Columns';
import { Dropdown } from '../Dropdown';
import { List } from '../List';
import { Tabs } from '../Tabs';

import { isLink, NavDataItems } from '../types';

import { useWindowSize } from 'react-use';
import styles from './NavList.module.scss';

// Matches the nav container's side padding, so a dropdown squeezed by a narrow
// window lines up with the bar. Keep in sync with `.Dropdown` max-width in
// NavList.module.scss.
const DROPDOWN_VIEWPORT_GUTTER = 40;

/**
 * Horizontal shift, in px, that keeps a dropdown inside the window. `left` is
 * the dropdown's viewport x. A dropdown wider than the space between the
 * gutters, which the CSS cap allows when a classic scrollbar takes up part of
 * `100vw`, is centered instead.
 */
export const getDropdownShift = (
  left: number,
  width: number,
  viewportWidth: number,
) => {
  const minLeft = DROPDOWN_VIEWPORT_GUTTER;
  const maxLeft = viewportWidth - DROPDOWN_VIEWPORT_GUTTER - width;

  if (maxLeft < minLeft) {
    return (viewportWidth - width) / 2 - left;
  }

  return Math.min(Math.max(left, minLeft), maxLeft) - left;
};

type NavListProps = PropsWithChildren<{
  items: NavDataItems;
  isNavVisible?: boolean;
}>;

export const NavList: FC<NavListProps> = ({
  children,
  items,
  isNavVisible = true,
}) => {
  const windowSize = useWindowSize();

  // COMMON
  const childrenCount = Children.count(children);
  const dropdownRefs = useRef<Array<HTMLDivElement>>(
    new Array(items.length + childrenCount),
  );
  const listItemsRefs = useRef<Array<HTMLLIElement>>(
    new Array(items.length + childrenCount),
  );

  const ddRefCb = (ref: HTMLDivElement) => {
    if (!ref) return;

    // const index = Array.from(ref.parentNode.children).indexOf(ref);
    const index = items.findIndex((item) => {
      return item.id === ref.getAttribute('id');
    });

    if (dropdownRefs.current && index !== -1) {
      dropdownRefs.current[childrenCount + index] = ref;
    }
  };

  const liRefCb = (ref: HTMLLIElement) => {
    if (!ref) return;

    const index = Array.from(ref.parentNode.children).indexOf(ref);

    listItemsRefs.current[index] = ref;
  };

  const prevIndex = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const focusFromKeyboardRef = useRef(false);

  const AnimatedDropdown = animated(Dropdown);

  // SPRINGS
  const [dropdownSprings] = useSprings<CSSProperties>(
    items.length,
    (i) => {
      // activeIndex includes childrenCount offset, so we need to add it to the spring index
      const isActive = i + childrenCount === activeIndex;

      return {
        opacity: isActive ? '1' : '0',
        pointerEvents: isActive ? 'all' : 'none',
        immediate: false,
        delay:
          isActive && prevIndex.current !== null && activeIndex !== null
            ? 100
            : undefined,
        config: {
          duration: 100,
          easing: easeInOut,
        },
      };
    },
    [activeIndex, childrenCount],
  );

  const [layerSpring] = useSpring<CSSProperties>(() => {
    const dropdown =
      activeIndex !== null ? dropdownRefs.current[activeIndex] : undefined;
    const dropdownType = dropdown?.getAttribute('data-type');

    let leftPositionDelta = 100;
    if (dropdownType === 'tabs') {
      leftPositionDelta = 130;
    }
    if (dropdownType === 'columns') {
      leftPositionDelta = 320;
    }

    const item =
      activeIndex !== null ? listItemsRefs.current[activeIndex] : undefined;
    const commonProps = {
      immediate: false,
      config: (key) => {
        if (
          (key === 'width' || key === 'height' || key === 'left') &&
          prevIndex.current === null
        ) {
          return {
            duration: 0,
          };
        }

        return {
          duration: 200,
          easing: easeInOut,
        };
      },
    };

    if (activeIndex === null || !dropdown || !item)
      return {
        translateY: '-8px',
        opacity: '0',
        pointerEvents: 'none',
        ...commonProps,
      };

    const width = dropdown.offsetWidth + 16;
    const shift = getDropdownShift(
      item.getBoundingClientRect().left - leftPositionDelta,
      width,
      document.documentElement.clientWidth,
    );

    return {
      width: `${width}px`,
      height: `${dropdown.offsetHeight + 24}px`,
      left: `${item.offsetLeft - leftPositionDelta + shift}px`,
      translateY: '0px',
      opacity: '1',
      pointerEvents: 'all',
      ...commonProps,
    };
  }, [activeIndex, windowSize.width, windowSize.height]);

  const focusTopLevelItem = (index: number) => {
    listItemsRefs.current[index]
      ?.querySelector<HTMLElement>('a, button')
      ?.focus();
  };

  const focusFirstDropdownLink = (index: number) => {
    dropdownRefs.current[index]
      ?.querySelector<HTMLElement>('a:not([data-hidden="true"])')
      ?.focus();
  };

  const closeDropdown = () => {
    prevIndex.current = null;
    setActiveIndex(null);
  };

  const openDropdown = (index: number) => {
    setActiveIndex((prevValue) => {
      prevIndex.current = prevValue;
      return index;
    });
  };

  // CLICK EVENTS
  const handleClick = (index: number) => {
    return (event: MouseEvent<HTMLButtonElement>) => {
      if (activeIndex === index) {
        closeDropdown();
        return;
      }

      openDropdown(index);

      // `detail` is 0 for Enter/Space activation. Keyboard users land in the
      // dropdown right away; mouse users keep focus on the button.
      if (event.detail === 0) {
        focusFirstDropdownLink(index);
      }
    };
  };

  const handleOutsideClick = () => {
    if (activeIndex !== null) {
      (document.activeElement as HTMLElement).blur();
      closeDropdown();
    }
  };

  const rootRef = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  // Close any open dropdown when the nav hides on scroll, so no expanded
  // invisible layer remains over the page and swallows clicks (SITE-6231).
  useEffect(() => {
    if (!isNavVisible && activeIndex !== null) {
      prevIndex.current = null;
      setActiveIndex(null);
    }
  }, [isNavVisible, activeIndex]);

  // Keyboard focus never opens a dropdown (WCAG 1.3.1 / 3.2.1, CCX-4752).
  // It only closes one left open when focus lands anywhere else in the nav,
  // including the logo and plain links, or leaves the nav entirely.
  const handleRootFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (!focusFromKeyboardRef.current || activeIndex === null) return;

    const target = event.target as Node;
    const stayedInActiveItem =
      listItemsRefs.current[activeIndex]?.contains(target) ||
      dropdownRefs.current[activeIndex]?.contains(target);

    if (!stayedInActiveItem) {
      closeDropdown();
    }
  };

  const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;

    if (activeIndex !== null && next && !rootRef.current?.contains(next)) {
      closeDropdown();
    }
  };

  // KEYBOARD
  const handleButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
    }
  };

  // ArrowDown also enters a dropdown the mouse already opened.
  const handleButtonKeyUp = (index: number) => {
    return (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowDown') return;

      if (activeIndex !== index) {
        openDropdown(index);
      }
      focusFirstDropdownLink(index);
    };
  };

  // Dropdown links are tabIndex -1, so Tab leaves the dropdown: back to the
  // owning button on Shift+Tab, forward to the next top-level item otherwise.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'Tab') {
      if (activeIndex === null) return;

      closeDropdown();

      if (event.shiftKey) {
        event.preventDefault();
        focusTopLevelItem(activeIndex);
        return;
      }

      const nextIndex = activeIndex + 1;
      if (nextIndex < childrenCount + items.length) {
        event.preventDefault();
        focusTopLevelItem(nextIndex);
      }
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    const currentDropdown =
      activeIndex !== null ? dropdownRefs.current[activeIndex] : undefined;

    if (!currentDropdown) return;

    const { key } = event;

    const selector = 'a:not([data-hidden="true"]), button';
    let focusableElements: HTMLElement[];

    if (currentDropdown.getAttribute('data-type') === 'tabs') {
      // focus order: tab buttons, active tab content links, featured column links
      const tabList = currentDropdown.querySelector('[class*="Tabs__tabList"]');
      const tabButtons = Array.from(
        tabList?.querySelectorAll<HTMLElement>('button') || [],
      );
      const activeTabContent = currentDropdown.querySelector<HTMLElement>(
        '[class*="Tabs__list_active"], [class*="Tabs__content_active"]',
      );
      const contentLinks = Array.from(
        activeTabContent?.querySelectorAll<HTMLElement>(selector) || [],
      );
      const featuredSection = currentDropdown.querySelector<HTMLElement>(
        '[class*="Tabs__featured"]',
      );
      const featuredLinks = Array.from(
        featuredSection?.querySelectorAll<HTMLElement>(selector) || [],
      );
      focusableElements = [...tabButtons, ...contentLinks, ...featuredLinks];
    } else {
      focusableElements = Array.from(
        currentDropdown.querySelectorAll(selector),
      );
    }

    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLElement,
    );
    const lastIndex = focusableElements.length - 1;

    if (key === 'ArrowUp') {
      const nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
      focusableElements[nextIndex]?.focus();
    } else if (key === 'ArrowDown') {
      const nextIndex =
        currentIndex === -1 || currentIndex >= lastIndex ? 0 : currentIndex + 1;
      focusableElements[nextIndex]?.focus();
    }
  };

  // Escape returns focus to the button that owns the open dropdown.
  const handleDocumentKeyUp = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && activeIndex !== null) {
        prevIndex.current = null;
        setActiveIndex(null);
        listItemsRefs.current[activeIndex]
          ?.querySelector<HTMLElement>('a, button')
          ?.focus();
      }
    },
    [activeIndex],
  );

  useEffect(() => {
    document.addEventListener('keyup', handleDocumentKeyUp);

    return () => {
      document.removeEventListener('keyup', handleDocumentKeyUp);
    };
  }, [handleDocumentKeyUp]);

  useEffect(() => {
    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Tab') {
        focusFromKeyboardRef.current = true;
      }
    };
    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, true);
    };
  }, []);

  const handleMouseDownCapture = useCallback(() => {
    focusFromKeyboardRef.current = false;
  }, []);

  return (
    <div
      ref={rootRef}
      className={styles.NavList}
      onMouseDownCapture={handleMouseDownCapture}
      onFocus={handleRootFocus}
      onBlur={handleRootBlur}
    >
      <ul className={styles.NavList__inner}>
        {Children.map(children, (child, i) => {
          return (
            <li
              key={`main-menu-nav-prefix-item-${i}`}
              ref={liRefCb}
              className={styles.NavList__item}
            >
              {child}
            </li>
          );
        })}

        {items.map((item, listIndex) => {
          const isActive = activeIndex === listIndex + childrenCount;

          return (
            <li
              ref={liRefCb}
              key={item.id}
              className={
                styles[`NavList__${'type' in item ? item.type : 'item'}`]
              }
            >
              {isLink(item) ? (
                <Link
                  href={item.href}
                  trackingId={item.trackingId}
                  locale={item.locale}
                >
                  <a className={styles.Link} role='link' tabIndex={0}>
                    <Typography
                      sx={{ whiteSpace: 'nowrap', mb: 0 }}
                      theme='main-menu-new'
                      variant='p'
                      component='span'
                    >
                      {item.title}
                    </Typography>
                  </a>
                </Link>
              ) : (
                <button
                  type='button'
                  className={styles.Button}
                  aria-haspopup={true}
                  aria-expanded={isActive}
                  aria-controls={item.id}
                  tabIndex={0}
                  onClick={handleClick(listIndex + childrenCount)}
                  onKeyDown={handleButtonKeyDown}
                  onKeyUp={handleButtonKeyUp(listIndex + childrenCount)}
                >
                  <Typography
                    sx={{ whiteSpace: 'nowrap', mb: 0 }}
                    theme='main-menu-new'
                    variant='p'
                    component='span'
                  >
                    {item.title}
                  </Typography>
                  <span className={styles.ChevronWrapper}>
                    <Icon
                      icon='ChevronDown'
                      className={cx(styles.Chevron, {
                        [styles['Chevron_hidden']]: isActive,
                      })}
                      sx={{ width: 12, height: 12 }}
                    />
                    <Icon
                      icon='ChevronUp'
                      className={cx(styles.Chevron, styles.Chevron_up, {
                        [styles['Chevron_hidden']]: !isActive,
                      })}
                      sx={{ width: 12, height: 12 }}
                    />
                  </span>
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <animated.div
        className={styles.AnimatedLayer}
        style={layerSpring}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        <div className={styles.AnimatedLayer__inner}>
          <div className={styles.AnimatedLayer__body}>
            {items.map((item, listIndex) => {
              if (isLink(item)) return null;

              const { type } = item;
              const index = listIndex + childrenCount;
              const isActive = activeIndex === index;
              const style = dropdownSprings[listIndex];

              const columns = 'columns' in item ? item.columns : undefined;
              const columnsWide =
                'columnsWide' in item ? item.columnsWide : undefined;

              const hasFeatured =
                type === 'tabs' && 'featured' in item && !!item.featured;

              return (
                <AnimatedDropdown
                  key={item.id}
                  ref={ddRefCb}
                  className={cx(styles.Dropdown, styles[`Dropdown_${type}`], {
                    [styles.Dropdown_twoColumns]: columns === 2 && !columnsWide,
                    [styles.Dropdown_twoColumnsWide]:
                      columns === 2 && columnsWide,
                    [styles.Dropdown_tabsWithFeatured]: hasFeatured,
                  })}
                  id={item.id}
                  style={style}
                  data-type={type}
                >
                  {type === 'list' && (
                    <List items={item.children} columns={columns} />
                  )}
                  {type === 'columns' && <Columns items={item.children} />}
                  {type === 'tabs' && (
                    <Tabs
                      items={item.children}
                      visible={isActive}
                      plainTabList={item.plainTabList}
                      featured={item.featured}
                      tabListLink={item.tabListLink}
                      tabListGradient={item.tabListGradient}
                    />
                  )}
                </AnimatedDropdown>
              );
            })}
          </div>
        </div>
      </animated.div>
    </div>
  );
};
